import React, { useState, useMemo, useEffect } from 'react';
import { useTeamChat } from '@/context/TeamChatContext';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Search, UserPlus, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface InviteMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channelId: string;
  channelName: string;
  currentMembers: string[];
  isGlobalDarkMode: boolean;
  children: React.ReactNode;
  isMobile?: boolean;
}

const InviteMemberDialog: React.FC<InviteMemberDialogProps> = ({
  open,
  onOpenChange,
  channelId,
  channelName,
  currentMembers,
  isGlobalDarkMode,
  children,
  isMobile: isMobileProp,
}) => {
  const { users, addMemberToChannel, currentUser } = useTeamChat();
  const [searchQuery, setSearchQuery] = useState('');
  const [inviting, setInviting] = useState<Set<string>>(new Set());
  const [isMobileState, setIsMobileState] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobileState(window.innerWidth < 640);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Use prop if provided, otherwise use state
  const isMobile = isMobileProp !== undefined ? isMobileProp : isMobileState;

  // Filter users who are not already members
  const availableUsers = useMemo(() => {
    return users.filter(user =>
      !currentMembers.includes(user.id) &&
      user.id !== currentUser?.id
    );
  }, [users, currentMembers, currentUser]);

  // Filter by search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return availableUsers;

    const query = searchQuery.toLowerCase();
    return availableUsers.filter(user =>
      user.displayName.toLowerCase().includes(query) ||
      user.name.toLowerCase().includes(query)
    );
  }, [availableUsers, searchQuery]);

  const handleInvite = async (userId: string) => {
    setInviting(prev => new Set(prev).add(userId));

    try {
      await addMemberToChannel(channelId, userId);

      const user = users.find(u => u.id === userId);
      toast.success(`Invited ${user?.displayName || 'user'} to ${channelName}`);

      // Remove from inviting set after a short delay
      setTimeout(() => {
        setInviting(prev => {
          const newSet = new Set(prev);
          newSet.delete(userId);
          return newSet;
        });
      }, 1000);
    } catch (error) {
      console.error('Error inviting member:', error);
      toast.error('Failed to invite member');
      setInviting(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const bgColor = isGlobalDarkMode ? 'bg-zinc-900' : 'bg-white';
  const inputBg = isGlobalDarkMode ? 'bg-zinc-800' : 'bg-gray-100';
  const hoverBg = isGlobalDarkMode ? 'hover:bg-zinc-800/70' : 'hover:bg-gray-100';
  const textColor = isGlobalDarkMode ? 'text-white' : 'text-gray-900';
  const mutedText = isGlobalDarkMode ? 'text-gray-400' : 'text-gray-600';

  const userListContent = (
    <>
      {/* Search Input */}
      <div className="relative">
        <Search className={cn("absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4", mutedText)} />
        <Input
          placeholder="Search members..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={cn(
            "pl-9 border-0 focus-visible:ring-0 focus-visible:ring-offset-0",
            inputBg,
            isGlobalDarkMode ? "text-white placeholder:text-gray-500" : "text-gray-900 placeholder:text-gray-500"
          )}
        />
      </div>

      {/* User List */}
      <div className="max-h-[300px] overflow-y-auto space-y-2 pr-1">
        {filteredUsers.length === 0 ? (
          <div className={cn("text-center py-8", mutedText)}>
            {searchQuery.trim() ? 'No members found' : 'All workspace members are already in this group'}
          </div>
        ) : (
          filteredUsers.map(user => {
            const isInvited = inviting.has(user.id);

            return (
              <div
                key={user.id}
                className={cn(
                  "flex items-center gap-3 p-3 rounded-lg transition-colors",
                  hoverBg
                )}
              >
                <Avatar className="w-9 h-9">
                  <AvatarImage src={user.avatar} alt={user.displayName} />
                  <AvatarFallback className={cn(
                    "text-sm",
                    isGlobalDarkMode ? "bg-zinc-700 text-gray-300" : "bg-gray-200 text-gray-700"
                  )}>
                    {user.displayName.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>

                <div className="flex-1 min-w-0">
                  <p className={cn("font-medium text-sm truncate", textColor)}>
                    {user.displayName}
                  </p>
                  <p className={cn("text-xs truncate", mutedText)}>
                    {user.name}
                  </p>
                </div>

                <Button
                  size="sm"
                  onClick={() => handleInvite(user.id)}
                  disabled={isInvited}
                  className={cn(
                    "gap-1.5",
                    isInvited
                      ? "bg-green-500 hover:bg-green-600 text-white"
                      : "bg-workspace-accent hover:bg-workspace-accent-hover text-white"
                  )}
                >
                  {isInvited ? (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      <span className="text-xs">Invited</span>
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-3.5 h-3.5" />
                      <span className="text-xs">Invite</span>
                    </>
                  )}
                </Button>
              </div>
            );
          })
        )}
      </div>
    </>
  );

  // Use Popover for both mobile and desktop
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        {children}
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          "p-0 border-0 shadow-xl",
          isMobile ? "w-[calc(100vw-2rem)] max-w-[340px]" : "w-[340px]",
          bgColor
        )}
        align="end"
        side="bottom"
        sideOffset={8}
      >
        <div className="p-4 space-y-4">
          {/* Header */}
          <div>
            <h3 className={cn("font-semibold text-base", textColor)}>
              Invite members
            </h3>
            <p className={cn("text-sm mt-1", mutedText)}>
              Search and invite workspace members to this group chat
            </p>
          </div>

          {userListContent}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default InviteMemberDialog;
